"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Heading3, Paragraph } from "../typography";
import { AustraliaMapMaskedCard } from "../australia-map-masked-card";

export const TheChallengeSolution = () => {
  useSectionNavTheme({
    trigger: "#ausenv-challenge-solution",
    isDark: true,
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      // Set initial opacity for cards 2 and 3, and solution content
      gsap.set([".australia-card-2", ".australia-card-3"], {
        opacity: 0,
      });

      // Initial reveal animation
      gsap.from(".challenge-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: "#ausenv-challenge-solution",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Pinned scroll animation
      const pinnedTl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: "#ausenv-challenge-solution",
          start: "top top",
          end: "bottom 95%",
          pin: ".map-cards",
          scrub: 1,
          invalidateOnRefresh: true,
          // markers: true,
        },
      });

      // Reveal Australia map cards in sequence
      pinnedTl
        .to(".australia-card-1", {
          opacity: 0,
        })
        .to(
          ".australia-card-1 .map-card__content",
          {
            y: -300,
          },
          "<"
        )
        .to(
          ".australia-card-2",
          {
            opacity: 1,
          },
          "<"
        )
        .to(".australia-card-2 .map-card__content", {
          y: -300,
        })
        .to([".australia-card-3"], {
          opacity: 1,
        });
    });
  });

  return (
    <section
      id="ausenv-challenge-solution"
      className="relative py-20 lg:py-32 xl:py-40"
    >
      <div className="absolute inset-0 dark:bg-gradient-to-l from-haizea-blue/50 via-haizea-dark to-slate-700/60"></div>
      <div className="container">
        <div className="flex items-start flex-col lg:flex-row gap-10 lg:gap-20">
          <div className="map-cards relative rounded-2xl border border-slate-200 dark:border-slate-500 w-full lg:w-[600px] lg:max-w-1/2 max-h-[60vh] aspect-square shrink-0 bg-slate-50/10 backdrop-blur-sm">
            <AustraliaMapMaskedCard
              title="5TB+"
              subtitle="Environmental data processed annually"
              className="australia-card-1 w-full h-full bg-linear-to-r from-haizea-orange via-90% via-haizea-green to-haizea-blue"
            />
            <AustraliaMapMaskedCard
              title="On-Demand"
              subtitle="Real-time analytics replace supercomputer"
              className="australia-card-2 w-full h-full bg-linear-to-r from-haizea-blue via-90% via-haizea-orange to-haizea-green"
            />
            <AustraliaMapMaskedCard
              title="Australia's Report"
              subtitle="Powered by Terrak.io"
              className="australia-card-3 w-full h-full bg-radial from-haizea-orange via-haizea-green to-haizea-blue"
            />
          </div>
          <div className="relative w-full">
            <ChallengeContent />
            <SolutionContent />
          </div>
        </div>
      </div>
    </section>
  );
};

const ChallengeContent = () => {
  return (
    <div className="space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="challenge-title text-slate-800 dark:text-slate-200">
        The Challenge
      </Heading3>
      <div className="">
        <div className="challenge-content space-y-4 lg:space-y-10">
          <Paragraph>
            Australia needed a way to track the environmental health of an
            entire continent in real-time. Traditional approaches required
            massive supercomputer resources to pre-calculate summaries, creating
            data management bottlenecks and limiting the ability to provide
            timely, accessible insights to researchers, managers, and the
            public.
          </Paragraph>

          <Paragraph>
            The Terrestrial Ecosystem Research Network (TERN) and Australian
            National University (ANU) were producing annual environmental
            condition reports, but faced significant technical challenges in
            processing and delivering insights from massive datasets spanning
            tens of sources and many terabytes of environmental data.
          </Paragraph>

          <Paragraph>
            The system needed to transform from resource-intensive batch
            processing to on-demand national-scale analysis, enabling
            interactive exploration of environmental trends and conditions
            across the entire continent.
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

const SolutionContent = () => {
  const solutionFeatures = [
    {
      title: "Massive Scale Processing",
      description:
        "Managing many terabytes of data from tens of environmental monitoring sources across the continent with automated workflows from data ingestion through to report generation.",
    },
    {
      title: "On-Demand Analytics",
      description:
        "Eliminating the need for supercomputer pre-calculation by enabling real-time analysis and summarization across spatial scales from national level down to individual catchments.",
    },
    {
      title: "Interactive Data Explorer",
      description:
        "Providing researchers and managers with powerful tools for custom analysis and data visualization, making complex environmental data accessible to diverse audiences.",
    },
  ];

  return (
    <div className="mt-10 lg:mt-40 space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="text-slate-800 dark:text-slate-200">
        The Solution
      </Heading3>
      <div className="">
        <div className="solution-content space-y-4 lg:space-y-10">
          <Paragraph>
            Haizea Analytics provides the complete data infrastructure powering
            Australia&apos;s Environment Report - from data storage and
            management to analytics and interactive delivery through the
            Terrak.io platform.
          </Paragraph>

          {solutionFeatures.map((feature, index) => (
            <div key={index} className="space-y-2">
              <h4 className="text-xl font-semibold text-haizea-green">
                {feature.title}
              </h4>
              <Paragraph>{feature.description}</Paragraph>
            </div>
          ))}

          <Paragraph>
            The Terrak.io platform solved critical data curation and processing
            challenges that were previously limiting the scope and accessibility
            of continental-scale environmental monitoring, transforming what
            previously required supercomputer resources into on-demand
            analytics.
          </Paragraph>
        </div>
      </div>
    </div>
  );
};
